// AI Generated Component
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { PAGE_FIX_EVENT } from '@client/app/modules/page-fix/constants/page-fix-event';
import { WizardContainerViewModelService } from '@client/app/modules/page-fix/forms/wizard-container/models/view-model.service';
import { PAGE_FIX_FORM_TAB_ICON } from '@client/app/modules/page-fix/constants/page-fix-form-tab-icon';
import { PageFixRepoService } from '@client/app/services/api/pagefix-repo/page-fix-repo.service';
import { AccessibilityChecksMapService } from '@client/app/modules/page-fix/components/accessibility-checks/accessibility-checks-map/accessibility-checks-map.service';
import { PAGE_FIX_FORM_VIEW_NAME } from '@client/app/modules/page-fix/constants/page-fix-form-view-name';

@Component({
    selector: 'page-fix-form-wizard-container',
    templateUrl: './wizard-container.component.html',
    standalone: false,
})
export class PageFixFormWizardContainerComponent implements OnInit, OnDestroy {
    @Input() formValid?: boolean | undefined;
    @Input() pageFixApplied?: boolean | undefined;
    @Input() pageFix?: any;
    @Output() close = new EventEmitter<any>();
    @Output() dismiss = new EventEmitter<any>();

    saving = false;
    destroying = false;
    registerViewId = 0;
    unregisterViewId = 0;
    requestDestroyId = 0;
    requestSubmitId = 0;
    updateViewId = 0;
    warningText = '';
    icon = PAGE_FIX_FORM_TAB_ICON;
    registeredViews: any[] = [];
    view: any = null;
    showNotification = false;
    pageFixSaved = false;
    private listenToUpdateWarning?: number;

    constructor (
        private monEventService: MonEventService,
        private wizardContainerViewModelService: WizardContainerViewModelService,
        private pageFixRepoService: PageFixRepoService,
        private accessibilityChecksMapService: AccessibilityChecksMapService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnDestroy (): void {
        this.onDestroy();
    }

    private activate (): void {
        this.saving = false;
        this.destroying = false;
        this.registerViewId = 0;
        this.unregisterViewId = 0;
        this.requestDestroyId = 0;
        this.requestSubmitId = 0;
        this.updateViewId = 0;
        this.warningText = '';
        this.icon = PAGE_FIX_FORM_TAB_ICON;
        this.registeredViews = [];
        this.view = null;
        this.showNotification = false;
        this.pageFixSaved = false;
        this.setupListeners();
    }

    private onDestroy (): void {
        this.monEventService.remove(this.updateViewId);
        this.monEventService.remove(this.registerViewId);
        this.monEventService.remove(this.unregisterViewId);
        this.monEventService.remove(this.requestSubmitId);
        this.monEventService.remove(this.requestDestroyId);
        if (this.listenToUpdateWarning) {
            this.monEventService.remove(this.listenToUpdateWarning);
        }
    }

    onTabChange (newView: any): void {
        this.view = newView;
        this.setViewVisibility();
    }

    private setupListeners (): void {
        this.updateViewId = this.monEventService.addListener(PAGE_FIX_EVENT.FORM_UPDATE_VIEW, (viewName: string) => {
            const targetView = this.registeredViews.find((v) => v.viewName === viewName);

            if (targetView) {
                this.onTabChange(targetView);
            }
        });

        this.registerViewId = this.monEventService.addListener(PAGE_FIX_EVENT.REGISTER_VIEW, (el: any, params: any) => {
            this.registerView(el, params);
        });

        this.unregisterViewId = this.monEventService.addListener(PAGE_FIX_EVENT.UNREGISTER_VIEW, (el: any) => {
            this.unregisterView(el);
        });

        this.requestSubmitId = this.monEventService.addListener(PAGE_FIX_EVENT.REQUEST_SUBMIT, (payload: any) => {
            this.saving = true;
            let promise: Promise<any>;
            if (this.pageFix) {
                promise = this.pageFixRepoService.update(this.pageFix, payload, {});
            } else {
                promise = this.pageFixRepoService.create(payload, undefined);
            }
            promise
                .then((pageFix: any) => {
                    if (!this.pageFixApplied) {
                        this.showNotification = true;
                        this.setDisableTimerForNotification();
                    }
                    this.accessibilityChecksMapService.addAccessibilityCheck({ id: pageFix.change.check_id } as any);
                    this.accessibilityChecksMapService.addUpdatedAccessibilityCheck({
                        id: pageFix.change.check_id,
                    } as any);
                    this.monEventService.run(PAGE_FIX_EVENT.UPDATE_PAGEFIX, pageFix);
                    const testingView = this.registeredViews.find((v) => v.viewName === PAGE_FIX_FORM_VIEW_NAME.TESTING);
                    this.onTabChange(testingView);
                    this.pageFixSaved = true;
                })
                .catch(() => {})
                .finally(() => {
                    this.saving = false;
                });
        });

        this.requestDestroyId = this.monEventService.addListener(PAGE_FIX_EVENT.REQUEST_DESTROY, () => {
            this.destroying = true;
            this.pageFixRepoService.destroy(this.pageFix.id).then(
                () => {
                    this.accessibilityChecksMapService.addDeletedPageFix(this.pageFix);
                    this.accessibilityChecksMapService.clearAccessibilityCheck(this.pageFix.change.check_id);
                    this.accessibilityChecksMapService.removeUpdatedAccessibilityCheck({
                        id: this.pageFix.change.check_id,
                    } as any);
                    this.close.emit({ pageFix: 'destroyed' });
                },
                () => {
                    this.destroying = false;
                },
            );
        });

        this.listenToUpdateWarning = this.monEventService.addListener(PAGE_FIX_EVENT.UPDATE_WARNING, (warningText: string) => {
            this.warningText = warningText;
        });
    }

    private registerView (el: any, params: any): void {
        const model = this.wizardContainerViewModelService.createViewModel(el, params);
        this.registeredViews.push(model);
        this.registeredViews.sort((a, b) => a.order - b.order);
        if (model.selected) {
            this.view = model;
        }
        this.setViewVisibility();
    }

    private unregisterView (element: any): void {
        for (let i = 0; i < this.registeredViews.length; i++) {
            const model = this.registeredViews[i];
            if (model.el === element) {
                this.registeredViews.splice(i, 1);
                break;
            }
        }
    }

    private setViewVisibility (): void {
        for (let i = 0; i < this.registeredViews.length; i++) {
            const view = this.registeredViews[i];
            if (view === this.view) {
                view.el.classList.remove('hidden');
            } else {
                view.el.classList.add('hidden');
            }
        }
    }

    private setDisableTimerForNotification (): void {
        setTimeout(() => {
            this.showNotification = false;
        }, 300000); // 5 minutes
    }

    trackByIndex (index: number): number {
        return index;
    }
}
